import { builder, Builder } from "@builder.io/react";
import Form from "./Form";
import PageHeader from "./PageHeader";
import CalloutBox from "./CallOutBox";

// Initialize Builder with your Public API Key
builder.init('0a08b1a9bc7c4241806bcb76ffb25d8c');

// Design Tokens
Builder.register("editor.settings", {
  styleStrictMode: true, // optional
  designTokens: {
    colors: [
      // PRIMARY COLORS
      { name: "Dark Purple", value: "var(--dark-purple, #35265F)" },
      { name: "Purple", value: "var(--purple, #675B8D)" },
      { name: "Light Purple", value: "var(--light-purple, #8E85A5)" },
      { name: "Red", value: "var(--red, #DE1F2F)" },
      { name: "Pure White", value: "var(--white, #FFFFFF)" },
      { name: "Blue", value: "var(--blue, #66D2F1)" },
      { name: "Light Blue", value: "var(--light-blue, #A8DFEF)" },
      { name: "Lighter Blue", value: "var(--lighter-blue, #D5EDF2)" },
      { name: "Faded Blue", value: "var(--faded-blue, #EEF8F9)" },
      { name: "Black", value: "var(--black, #333)" },
      { name: "Dark Gray", value: "var(--dark-gray, #58595B)" },
      { name: "Gray", value: "var(--gray, #D2D5DA)" },
      { name: "Light Gray", value: "var(--light-gray, #EEEFF1)" },
      { name: "Lighter Gray", value: "var(--lighter-gray, #F9F9F9)" },
      { name: "P4 Red", value: "var(--p4-red, #D31E31)" },
      { name: "P4 Purple", value: "var(--p4-purple, #4F2CBC)" },
      // SECONDARY COLORS
      { name: "Yellow", value: "var(--yellow, #F2C71B)" },
      { name: "Pink", value: "var(--pink, #B372EC)" },
      { name: "Magenta", value: "var(--magenta, #B5255F)" },
    ],
    spacing: [
      { name: "Large", value: "var(--space-large, 20px)" },
      { name: "Small", value: "var(--space-small, 10px)" },
      { name: "Tiny", value: "5px" }
    ],
    fontFamily: [
      { "name": "Poppins", "value": "Poppins, sans-serif" },
      { "name": "Roboto Slab", "value": "Roboto Slab, serif" },
    ],
    fontSize: [
      // Font Sizes - Desktop
      { name: "Desktop Header - H1", value: "52px" },
      { name: "XL Header - H2", value: "40px" },
      { name: "L Header - H3", value: "35px" },
      { name: "M Header - H4", value: "28px" },
      { name: "S Header - H5", value: "24px" },
      { name: "XS Header - H6", value: "21px" },

      // Font Sizes - Mobile
      { name: "Mobile Header - H1", value: "48px" },
    ]
  },
});
Builder.registerComponent(Form, {
  name: 'Slides',
  inputs: [
    {
      name: 'slides',
      type: 'list',
      subFields: [
        { name: 'key', type: 'string' },
        { name: 'question', type: 'string' },
        {
          name: 'type',
          type: 'string',
          enum: ['multiple', 'multiple-fields'],
          defaultValue: 'multiple',
        },
        {
          name: 'Answers',
          type: 'list',
          showIf: (step) => {
            try {
              const obj = Object.fromEntries(step._data || []);
              const value = obj?.type?.value?.storedValue;
              return value === 'multiple';
            } catch {
              return false;
            }
          },
          subFields: [
            { name: 'option', type: 'richText' },
          ]
        },
        {
          name: 'fields',
          friendlyName: 'Input Fields',
          type: 'list',
          showIf: (step) => {
            try {
              const obj = Object.fromEntries(step._data || []);
              const value = obj?.type?.value?.storedValue;
              return value === 'multiple-fields';
            } catch {
              return false;
            }
          },
          subFields: [
            { name: 'label', type: 'string' },
            { name: 'key', type: 'string' },
            {
              name: 'type',
              type: 'string',
              enum: ['text', 'tel', 'email', 'number'],
              defaultValue: 'text',
            },
          ],
        },
      ],
    },
    { name: 'showProgressBar', type: 'boolean', defaultValue: true },
    { name: 'showProgressText', type: 'boolean', defaultValue: true },
    { name: 'progressBarText', type: 'string' },
    { name: 'questionFontSize', type: 'string', defaultValue: '28px', helperText: "Mobile font size. Desktop font size is set via CSS." },
    { name: 'questionColor', type: 'color', defaultValue: '#35265F' },
    { name: 'answerFontSize', type: 'string', defaultValue: '21px' },
    { name: 'answerTextColor', type: 'color', defaultValue: '#35265F' },
    { name: 'answerBgColor', type: 'color', defaultValue: '#66D2F1' },
    { name: 'answerSelectedBgColor', type: 'color', defaultValue: '#66D2F1' },
    { name: 'answerSelectedTextColor', type: 'color', defaultValue: '#35265F' },
    { name: 'answerHoverBgColor', type: 'color', defaultValue: '#DAED5EDF28FF' },
    { name: 'answerHoverTextColor', type: 'color', defaultValue: '#35265F' },
    { name: 'disclaimerText', type: 'richText', helperText: "Text to display below the submit button on the last slide." },
  ]
});

Builder.registerComponent(PageHeader, {
  name: 'PageHeader',
  inputs: [{ name: 'logo', type: 'file' },
  { name: 'logoLink', type: 'url' },
  { name: 'ctaText', type: 'string' },
  { name: 'buttonText', type: 'string' },
  { name: 'buttonLink', type: 'url', helperText: "Button link must start with 'tel:' for phone numbers or 'http://'/'https://' for web links. Phone icon is added automatically if the link starts with 'tel:'." },

  ],
});

Builder.registerComponent(CalloutBox, {
  name: "Callout Box",
  inputs: [
    { name: "badgeText", type: "string", defaultValue: "75% off our top products" },
    { name: "iconSvg", type: "longText", defaultValue: "" },

    { name: "codePrefix", type: "string", defaultValue: "with code:" },
    { name: "codeValue", type: "string", defaultValue: "OCTOBER25" },

    { name: "ctaText", type: "string", defaultValue: "Questions? Call now:" },
    { name: "buttonText", type: "string", defaultValue: "1 (800) 742-0124" },
    { name: "buttonLink", type: "string", defaultValue: "tel:8007420124" },

    { name: "buttonClassName", type: "string", defaultValue: "button-secondary" },
    { name: "className", type: "string", defaultValue: "" },
  ],
});